.root {
  text-box-edge: cap alphabetic;
  text-box-trim: trim-both;
}

.root:has(> .truncate) {
  display: inline-grid;
}

.truncate {
  text-overflow: ellipsis;
  overflow: clip visible;
  white-space: nowrap;
  text-box-edge: inherit;
  text-box-trim: inherit;
  min-width: 0;
}

.transform-capitalize {
  text-transform: capitalize;
}

.transform-uppercase {
  text-transform: uppercase;
}

.transform-lowercase {
  text-transform: lowercase;
}

.decoration-none {
  text-decoration: none;
}

.decoration-underline {
  text-decoration: underline;
}

.decoration-line-through {
  text-decoration: line-through;
}

.text-align-start {
  text-align: start;
}

.text-align-end {
  text-align: end;
}

.text-align-center {
  text-align: center;
}

.white-space-nowrap {
  white-space: nowrap;
}

.white-space-normal {
  white-space: normal;
}

.white-space-pre-wrap {
  white-space: pre-wrap;
}

.white-space-preserve {
  white-space: preserve;
}

.word-wrap-break-word {
  word-wrap: break-word;
}

.weight-regular {
  font-weight: var(--font-weight__regular);
}

.weight-medium {
  font-weight: var(--font-weight__medium);
}

/* `FontWeight` has named `semibold` since the token landed, but no class carried it, so
   `weight="semibold"` rendered the element's default weight — a bold `h1` stayed bold. In the
   product font it and `bold` are the same face regardless: `fonts.css` loads no 700. */
.weight-semibold {
  font-weight: var(--font-weight__semibold);
}

.weight-bold {
  font-weight: var(--font-weight__bold);
}

.size-xs {
  font-size: var(--font-size__xs);
}

.size-sm {
  font-size: var(--font-size__sm);
}

.size-md {
  --ds-min-font-size: var(--font-size__sm);
  --ds-max-font-size: var(--font-size__md);
}

.size-md:not(.fluid) {
  font-size: var(--font-size__md);
}

.size-lg {
  --ds-min-font-size: var(--font-size__sm);
  --ds-max-font-size: var(--font-size__lg);
}

.size-lg:not(.fluid) {
  font-size: var(--font-size__lg);
}

.size-xl {
  --ds-min-font-size: var(--font-size__md);
  --ds-max-font-size: var(--font-size__xl);
}

.size-xl:not(.fluid) {
  font-size: var(--font-size__xl);
}

.size-2xl {
  --ds-min-font-size: var(--font-size__lg);
  --ds-max-font-size: var(--font-size__2xl);
}

.size-2xl:not(.fluid) {
  font-size: var(--font-size__2xl);
}

.size-3xl {
  --ds-min-font-size: var(--font-size__xl);
  --ds-max-font-size: var(--font-size__3xl);
}

.size-3xl:not(.fluid) {
  font-size: var(--font-size__3xl);
}

/** https://www.alwaystwisted.com/articles/building-fluid-typographic-scales-with-clamp-and-heading */
.fluid {
  font-size: clamp(
    var(--ds-min-font-size),
    calc(
      var(--ds-min-font-size) + (var(--ds-max-font-size) - var(--ds-min-font-size)) *
        ((100vw - var(--breakpoint__md)) / (var(--breakpoint__2xl) - var(--breakpoint__md)))
    ),
    var(--ds-max-font-size)
  );
}

.height-none {
  line-height: var(--line-height__none);
}

.height-3xs {
  line-height: var(--line-height__3xs);
}

.height-2xs {
  line-height: var(--line-height__2xs);
}

.height-xs {
  line-height: var(--line-height__xs);
}

.height-sm {
  line-height: var(--line-height__sm);
}

.height-md {
  line-height: var(--line-height__md);
}

.height-lg {
  line-height: var(--line-height__lg);
}

.height-xl {
  line-height: var(--line-height__xl);
}

.height-2xl {
  line-height: var(--line-height__2xl);
}

.height-3xl {
  line-height: var(--line-height__3xl);
}

.tabular-nums {
  font-variant-numeric: tabular-nums;
}

.text-wrap-pretty {
  text-wrap: pretty;
}

.text-wrap-balance {
  text-wrap: balance;
}

.family-primary {
  font-family: var(--font-family__primary), sans-serif;
}

/* No `.family-decorative`. `--font-family__decorative` still exists and is still used, but by one
   legacy component that reaches for the variable directly (`content/texts/bou-title/title-style.ts`).
   Exposing it as a `Text family="decorative"` prop gave new UI a second heading font to reach for,
   which is the opposite of what the token is for. */
.family-monospace {
  font-family: var(--font-family__monospace), monospace;
}

.color-black {
  color: var(--color__black);
}

.color-blue-50 {
  color: var(--color__blue-50);
}

.color-blue-100 {
  color: var(--color__blue-100);
}

.color-blue-200 {
  color: var(--color__blue-200);
}

.color-blue-300 {
  color: var(--color__blue-300);
}

.color-blue-400 {
  color: var(--color__blue-400);
}

.color-blue-500 {
  color: var(--color__blue-500);
}

.color-blue-600 {
  color: var(--color__blue-600);
}

.color-blue-700 {
  color: var(--color__blue-700);
}

.color-blue-800 {
  color: var(--color__blue-800);
}

.color-blue-900 {
  color: var(--color__blue-900);
}

.color-blue-950 {
  color: var(--color__blue-950);
}

.color-gray-50 {
  color: var(--color__gray-50);
}

.color-gray-100 {
  color: var(--color__gray-100);
}

.color-gray-200 {
  color: var(--color__gray-200);
}

.color-gray-300 {
  color: var(--color__gray-300);
}

.color-gray-400 {
  color: var(--color__gray-400);
}

.color-gray-500 {
  color: var(--color__gray-500);
}

.color-gray-600 {
  color: var(--color__gray-600);
}

.color-gray-700 {
  color: var(--color__gray-700);
}

.color-gray-800 {
  color: var(--color__gray-800);
}

.color-gray-900 {
  color: var(--color__gray-900);
}

.color-gray-950 {
  color: var(--color__gray-950);
}

.color-green-50 {
  color: var(--color__green-50);
}

.color-green-100 {
  color: var(--color__green-100);
}

.color-green-200 {
  color: var(--color__green-200);
}

.color-green-300 {
  color: var(--color__green-300);
}

.color-green-400 {
  color: var(--color__green-400);
}

.color-green-500 {
  color: var(--color__green-500);
}

.color-green-600 {
  color: var(--color__green-600);
}

.color-green-700 {
  color: var(--color__green-700);
}

.color-green-800 {
  color: var(--color__green-800);
}

.color-green-900 {
  color: var(--color__green-900);
}

.color-green-950 {
  color: var(--color__green-950);
}

.color-lilac-50 {
  color: var(--color__lilac-50);
}

.color-lilac-100 {
  color: var(--color__lilac-100);
}

.color-lilac-200 {
  color: var(--color__lilac-200);
}

.color-lilac-300 {
  color: var(--color__lilac-300);
}

.color-lilac-400 {
  color: var(--color__lilac-400);
}

.color-lilac-500 {
  color: var(--color__lilac-500);
}

.color-lilac-600 {
  color: var(--color__lilac-600);
}

.color-lilac-700 {
  color: var(--color__lilac-700);
}

.color-lilac-800 {
  color: var(--color__lilac-800);
}

.color-lilac-900 {
  color: var(--color__lilac-900);
}

.color-lilac-950 {
  color: var(--color__lilac-950);
}

.color-orange-50 {
  color: var(--color__orange-50);
}

.color-orange-100 {
  color: var(--color__orange-100);
}

.color-orange-200 {
  color: var(--color__orange-200);
}

.color-orange-300 {
  color: var(--color__orange-300);
}

.color-orange-400 {
  color: var(--color__orange-400);
}

.color-orange-500 {
  color: var(--color__orange-500);
}

.color-orange-600 {
  color: var(--color__orange-600);
}

.color-orange-700 {
  color: var(--color__orange-700);
}

.color-orange-800 {
  color: var(--color__orange-800);
}

.color-orange-900 {
  color: var(--color__orange-900);
}

.color-orange-950 {
  color: var(--color__orange-950);
}

.color-pink-50 {
  color: var(--color__pink-50);
}

.color-pink-100 {
  color: var(--color__pink-100);
}

.color-pink-200 {
  color: var(--color__pink-200);
}

.color-pink-300 {
  color: var(--color__pink-300);
}

.color-pink-400 {
  color: var(--color__pink-400);
}

.color-pink-500 {
  color: var(--color__pink-500);
}

.color-pink-600 {
  color: var(--color__pink-600);
}

.color-pink-700 {
  color: var(--color__pink-700);
}

.color-pink-800 {
  color: var(--color__pink-800);
}

.color-pink-900 {
  color: var(--color__pink-900);
}

.color-pink-950 {
  color: var(--color__pink-950);
}

.color-purple-50 {
  color: var(--color__purple-50);
}

.color-purple-100 {
  color: var(--color__purple-100);
}

.color-purple-200 {
  color: var(--color__purple-200);
}

.color-purple-300 {
  color: var(--color__purple-300);
}

.color-purple-400 {
  color: var(--color__purple-400);
}

.color-purple-500 {
  color: var(--color__purple-500);
}

.color-purple-600 {
  color: var(--color__purple-600);
}

.color-purple-700 {
  color: var(--color__purple-700);
}

.color-purple-800 {
  color: var(--color__purple-800);
}

.color-purple-900 {
  color: var(--color__purple-900);
}

.color-purple-950 {
  color: var(--color__purple-950);
}

.color-red-50 {
  color: var(--color__red-50);
}

.color-red-100 {
  color: var(--color__red-100);
}

.color-red-200 {
  color: var(--color__red-200);
}

.color-red-300 {
  color: var(--color__red-300);
}

.color-red-400 {
  color: var(--color__red-400);
}

.color-red-500 {
  color: var(--color__red-500);
}

.color-red-600 {
  color: var(--color__red-600);
}

.color-red-700 {
  color: var(--color__red-700);
}

.color-red-800 {
  color: var(--color__red-800);
}

.color-red-900 {
  color: var(--color__red-900);
}

.color-red-950 {
  color: var(--color__red-950);
}

.color-teal-50 {
  color: var(--color__teal-50);
}

.color-teal-100 {
  color: var(--color__teal-100);
}

.color-teal-200 {
  color: var(--color__teal-200);
}

.color-teal-300 {
  color: var(--color__teal-300);
}

.color-teal-400 {
  color: var(--color__teal-400);
}

.color-teal-500 {
  color: var(--color__teal-500);
}

.color-teal-600 {
  color: var(--color__teal-600);
}

.color-teal-700 {
  color: var(--color__teal-700);
}

.color-teal-800 {
  color: var(--color__teal-800);
}

.color-teal-900 {
  color: var(--color__teal-900);
}

.color-teal-950 {
  color: var(--color__teal-950);
}

.color-white {
  color: var(--color__white);
}

.color-yellow-50 {
  color: var(--color__yellow-50);
}

.color-yellow-100 {
  color: var(--color__yellow-100);
}

.color-yellow-200 {
  color: var(--color__yellow-200);
}

.color-yellow-300 {
  color: var(--color__yellow-300);
}

.color-yellow-400 {
  color: var(--color__yellow-400);
}

.color-yellow-500 {
  color: var(--color__yellow-500);
}

.color-yellow-600 {
  color: var(--color__yellow-600);
}

.color-yellow-700 {
  color: var(--color__yellow-700);
}

.color-yellow-800 {
  color: var(--color__yellow-800);
}

.color-yellow-900 {
  color: var(--color__yellow-900);
}

.color-yellow-950 {
  color: var(--color__yellow-950);
}
